import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Grid, Stack, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
    AddCircleOutline,
    DeleteOutline,
    Download,
    PlaylistAddCheck,
    Rule,
    Sync,
    UploadFile,
} from '@mui/icons-material';
import { Alert } from 'c-components';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { useUserPermissions } from 'c-auth-module/Hooks';
import type { Campaign, CampaignInsertionOrderPlaceholder, InsertionOrderKey } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { useEntityData } from 'c-data';
import { useCommonTranslation } from 'c-translation';
import CustomFooter from 'c-main/Components/Schedule/Components/LineItems/CustomFooter';
import ScheduleList from 'c-main/Components/Schedule/Components/Schedule/ScheduleList';
import DeleteScheduleDialog from 'c-main/Components/Schedule/Components/Schedule/DeleteScheduleDialog';
import useNewCampaignSchedule from 'c-main/Components/Schedule/useNewCampaignSchedule';
import { scheduleName } from 'c-main/Components/Schedule/lib';
import type { ScheduleVariant } from './ScheduleVariantContext';
import { ScheduleVariantProvider } from './ScheduleVariantContext';
import useDspTargeting from './useDspTargeting';
import InsertionOrderTable from './InsertionOrderTable';
import AllowedVenuesTable from './AllowedVenuesTable';
import InsertionOrderSelectionDialog from './InsertionOrderSelectionDialog';
import InsertionOrderRulesDialog from './InsertionOrderRulesDialog';
import AllowedVenueDeleteDialog from './AllowedVenueDeleteDialog';
import AllowedVenueUploadDialog from './AllowedVenueUploadDialog';
import { AddPlaceholderDialog, DeletePlaceholderDialog } from './PlaceholderDialogs';
import { insertionOrdersToCsv } from './lib';
import { selectedInsertionOrderKeys } from './selectionChanges';

type Props = {
    id: number;
};

const viewPermissions = [PermissionName.Pop_displaysCampaign_insertion_ordersRead];
const syncPermissions = [PermissionName.Pop_displaysCampaign_insertion_ordersSync];
const venueUploadPermissions = [PermissionName.Pop_displaysCampaign_allowed_venuesCreate];
const schedulePermissions = [PermissionName.UniledportalCampaign_insertion_order_scheduleUpdate];
/** CPM and the impressions target are both insertion order details (UL-8133). */
const detailPermissions = [PermissionName.Pop_displaysCampaign_insertion_order_detailsUpdate];
const rulePermissions = [
    PermissionName.Pop_displaysCampaign_insertion_order_parametersCreate,
    PermissionName.Pop_displaysCampaign_insertion_order_parametersDestroy,
];

const containerSx = { height: '100%' };
const leftColSx = { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 };
const rightColSx = { height: '100%', display: 'flex', minHeight: 0 };

/**
 * UL-7912 -- the schedule area for an insertion-order targeting (`vistar_us2`) campaign.
 *
 * The direct page's `CampaignSchedule` is not rendered at all for these campaigns: it fires the
 * direct page's six requests on mount and fills shared state with displays and line items. This
 * container fetches schedules only, and keeps its own insertion order data and selection.
 *
 * The right rail and rule editor are the shared components, told they are on a DSP page through
 * `ScheduleVariantProvider`.
 */
const DspCampaignSchedule: React.FC<Props> = ({ id }) => {
    const t = useCommonTranslation();
    const { hasAll } = useUserPermissions();
    const targeting = useDspTargeting(id);

    const {
        schedules,
        getSchedules,
        schedulesCurrentPage,
        schedulesPerPage,
        scheduleSearch,
        updateSchedules,
        removeSchedules,
    } = useNewCampaignSchedule(id);

    const [tab, setTab] = useState('insertionOrders');
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [venuesReloadToken, setVenuesReloadToken] = useState(0);
    const [notice, setNotice] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [dialog, setDialog] = useState<
        'selection' | 'parameters' | 'venues' | 'deleteVenues' | 'addPlaceholder' | null
    >(null);
    /** UL-8136 -- the planned line being linked (selection dialog in link mode) or deleted. */
    const [linking, setLinking] = useState<CampaignInsertionOrderPlaceholder | null>(null);
    const [deleting, setDeleting] = useState<CampaignInsertionOrderPlaceholder | null>(null);
    const closeDialog = useCallback(() => setDialog(null), []);

    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id });

    const loadSchedules = useCallback(
        () => getSchedules(schedulesCurrentPage, schedulesPerPage, scheduleSearch),
        [getSchedules, schedulesCurrentPage, schedulesPerPage, scheduleSearch],
    );

    useEffect(() => {
        setSelectedKeys([]);
        loadSchedules();
        // Schedules only -- never the direct page's fetchData.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    /** Everything that can move when a schedule changes: the schedules and every row's chip. */
    const refreshAll = useCallback(async () => {
        await Promise.all([loadSchedules(), targeting.refresh()]);
    }, [loadSchedules, targeting]);

    const selectedInsertionOrders = useMemo(
        () => selectedInsertionOrderKeys(selectedKeys, targeting.scheduleRowsByKey),
        [selectedKeys, targeting.scheduleRowsByKey],
    );

    const variant = useMemo<ScheduleVariant>(
        () => ({
            variant: 'dsp',
            selectedCount: selectedInsertionOrders.length,
            attachPermissions: schedulePermissions,
            onAttach: async scheduleId => {
                if (await targeting.applySchedule(scheduleId, selectedInsertionOrders)) {
                    setNotice(
                        t('Modules.Main.Campaigns.CampaignSchedule.dsp.schedule.applied', {
                            count: selectedInsertionOrders.length,
                        }),
                    );
                }
            },
            onDetach: async scheduleId => {
                // Only the ticked rows currently on THIS schedule move; rows on other schedules stay.
                if (await targeting.returnToDefault(selectedInsertionOrders, scheduleId)) {
                    setNotice(
                        t('Modules.Main.Campaigns.CampaignSchedule.dsp.schedule.removed', {
                            count: selectedInsertionOrders.length,
                        }),
                    );
                }
            },
        }),
        [selectedInsertionOrders, targeting, t],
    );

    const deleteSchedule = useMemo(
        () => (deleteId != null ? schedules.find(s => s.id === deleteId) : null),
        [schedules, deleteId],
    );

    /** Deleting a schedule moves its insertion orders back to the Default on the API, so the chips are stale. */
    const onScheduleRemoved = useCallback(
        (scheduleIds: number[]) => {
            removeSchedules(scheduleIds);
            targeting.refresh();
        },
        [removeSchedules, targeting],
    );

    const onSync = useCallback(async () => {
        if (await targeting.sync()) {
            setNotice(t('Modules.Main.Campaigns.CampaignSchedule.dsp.toolbar.syncDone'));
        }
    }, [targeting, t]);

    const onLink = useCallback(
        async (io: InsertionOrderKey) => {
            if (!linking) return null;
            const refused = await targeting.linkPlaceholder(linking.id, io);
            if (refused === null) {
                setLinking(null);
                setNotice(t('Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.linked'));
            }
            return refused;
        },
        [linking, t, targeting],
    );

    const onDownload = useCallback(() => {
        const csv = insertionOrdersToCsv(
            targeting.insertionOrders,
            targeting.rulesById,
            new Map(schedules.map(s => [s.id, scheduleName(s, t)])),
        );
        const url = window.URL.createObjectURL(
            new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `campaign-${id}-insertion-orders.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, [id, schedules, t, targeting.insertionOrders, targeting.rulesById]);

    const toolbar = (
        <Stack direction="row" gap={1} flexWrap="wrap">
            <IfHasAllPermissions permissions={rulePermissions}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlaylistAddCheck />}
                    onClick={() => setDialog('selection')}
                >
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.toolbar.selection')}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Rule />}
                    onClick={() => setDialog('parameters')}
                >
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.toolbar.editParameters')}
                </Button>
            </IfHasAllPermissions>
            <IfHasAllPermissions permissions={detailPermissions}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircleOutline />}
                    onClick={() => setDialog('addPlaceholder')}
                >
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.placeholders.add')}
                </Button>
            </IfHasAllPermissions>
            <IfHasAllPermissions permissions={venueUploadPermissions}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFile />}
                    onClick={() => setDialog('venues')}
                >
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.toolbar.uploadVenues')}
                </Button>
                {targeting.venueCount > 0 && (
                    <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={() => setDialog('deleteVenues')}
                    >
                        {t('Modules.Main.Campaigns.CampaignSchedule.dsp.toolbar.deleteVenues')}
                    </Button>
                )}
            </IfHasAllPermissions>
            <IfHasAllPermissions permissions={syncPermissions}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={targeting.isSyncing ? <CircularProgress size={14} /> : <Sync />}
                    disabled={targeting.isSyncing}
                    onClick={onSync}
                >
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.toolbar.sync')}
                </Button>
            </IfHasAllPermissions>
            <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                disabled={targeting.insertionOrders.length === 0}
                onClick={onDownload}
            >
                {t('Modules.Main.Campaigns.CampaignSchedule.dsp.toolbar.download')}
            </Button>
        </Stack>
    );

    return (
        <IfHasAllPermissions
            permissions={viewPermissions}
            otherwise={
                <Alert severity="info">
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.noPermission')}
                </Alert>
            }
        >
            <ScheduleVariantProvider value={variant}>
                <InsertionOrderSelectionDialog
                    campaignId={id}
                    open={dialog === 'selection'}
                    onClose={closeDialog}
                    dateStart={campaign?.date_start}
                    dateEnd={campaign?.date_end}
                    targeting={targeting}
                    onApplied={setNotice}
                />
                <InsertionOrderSelectionDialog
                    campaignId={id}
                    open={linking != null}
                    onClose={() => setLinking(null)}
                    dateStart={campaign?.date_start}
                    dateEnd={campaign?.date_end}
                    targeting={targeting}
                    mode="link"
                    onPick={onLink}
                />
                <AddPlaceholderDialog
                    open={dialog === 'addPlaceholder'}
                    onClose={closeDialog}
                    onSave={targeting.createPlaceholder}
                    busy={targeting.isSavingPlaceholder}
                    error={targeting.placeholderError}
                />
                <DeletePlaceholderDialog
                    placeholder={deleting}
                    onClose={() => setDeleting(null)}
                    onConfirm={placeholder => targeting.deletePlaceholder(placeholder.id)}
                    busy={targeting.isSavingPlaceholder}
                    error={targeting.placeholderError}
                />
                <InsertionOrderRulesDialog
                    campaignId={id}
                    open={dialog === 'parameters'}
                    onClose={closeDialog}
                    dateStart={campaign?.date_start}
                    dateEnd={campaign?.date_end}
                    targeting={targeting}
                />
                <AllowedVenueUploadDialog
                    campaignId={id}
                    open={dialog === 'venues'}
                    onClose={closeDialog}
                    onSaved={() => {
                        targeting.refreshVenues();
                        setVenuesReloadToken(token => token + 1);
                        setNotice(t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.saved'));
                    }}
                />
                <AllowedVenueDeleteDialog
                    campaignId={id}
                    venueCount={targeting.venueCount}
                    open={dialog === 'deleteVenues'}
                    onClose={closeDialog}
                    onDeleted={() => {
                        targeting.refreshVenues();
                        setVenuesReloadToken(token => token + 1);
                        setNotice(
                            t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.delete.done'),
                        );
                    }}
                />
                {deleteSchedule != null && (
                    <DeleteScheduleDialog
                        campaignId={id}
                        scheduleId={deleteId}
                        schedule={deleteSchedule}
                        onClose={() => setDeleteId(null)}
                        removeSchedules={onScheduleRemoved}
                    />
                )}
                <Grid container spacing={2} sx={containerSx} alignItems="stretch">
                    <Grid item xs={12} md={6} lg={8} xl={9} sx={leftColSx}>
                        {notice && (
                            <Alert
                                severity="success"
                                onClose={() => setNotice(null)}
                                sx={{ mb: 1 }}
                            >
                                {notice}
                            </Alert>
                        )}
                        <TabContext value={tab}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <TabList onChange={(_, value) => setTab(value)}>
                                    <Tab
                                        value="insertionOrders"
                                        label={t(
                                            'Modules.Main.Campaigns.CampaignSchedule.dsp.insertionOrdersTab',
                                            {
                                                count: targeting.insertionOrders.length,
                                            },
                                        )}
                                    />
                                    <Tab
                                        value="venues"
                                        label={t(
                                            'Modules.Main.Campaigns.CampaignSchedule.dsp.allowedVenuesTab',
                                            {
                                                count: targeting.venueCount,
                                            },
                                        )}
                                    />
                                </TabList>
                            </Box>
                            <TabPanel value="insertionOrders" sx={{ px: 0, flex: 1, minHeight: 0 }}>
                                <InsertionOrderTable
                                    campaignId={id}
                                    targeting={targeting}
                                    schedules={schedules}
                                    selectedKeys={selectedKeys}
                                    onSelectionChange={setSelectedKeys}
                                    toolbar={toolbar}
                                    canEditDetails={hasAll(detailPermissions)}
                                    canSchedule={hasAll(schedulePermissions)}
                                    onNotice={setNotice}
                                    onLinkPlaceholder={setLinking}
                                    onDeletePlaceholder={setDeleting}
                                />
                            </TabPanel>
                            <TabPanel value="venues" sx={{ px: 0 }}>
                                <AllowedVenuesTable
                                    campaignId={id}
                                    reloadToken={venuesReloadToken}
                                />
                            </TabPanel>
                        </TabContext>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4} xl={3} sx={rightColSx}>
                        <Box sx={{ width: '100%', overflow: 'auto' }}>
                            {schedules.length > 0 && (
                                <>
                                    <ScheduleList
                                        id={id}
                                        onDelete={setDeleteId}
                                        updateSchedules={updateSchedules}
                                        fetchData={refreshAll}
                                    />
                                    <Box
                                        sx={{
                                            position: 'sticky',
                                            bottom: 0,
                                            backgroundColor: 'background.paper',
                                            zIndex: 1,
                                        }}
                                    >
                                        <CustomFooter campaignId={id} paginationType="schedules" />
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </ScheduleVariantProvider>
        </IfHasAllPermissions>
    );
};

export default DspCampaignSchedule;
